import { MaterialIcons } from "@expo/vector-icons";
import _ from "lodash";
// eslint-disable-next-line import/no-named-default
import { default as MomentLib } from "moment";
import { extendMoment } from "moment-range";
import { Box, Button, useTheme, VStack } from "native-base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, DateData, LocaleConfig } from "react-native-calendars";
import type { MarkingProps } from "react-native-calendars/src/calendar/day/marking";

import { Scale } from "../constants";
import { formatMomentAsBackendFormatDateString } from "../helpers/generalHelpers";
import logger from "../services/logger";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const moment = extendMoment(MomentLib);

// Populate LocaleConfig for English and Dutch
LocaleConfig.locales.en = {
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  today: "Today",
};

LocaleConfig.locales.nl = {
  monthNames: [
    "Januari",
    "Februari",
    "Maart",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Augustus",
    "September",
    "Oktober",
    "November",
    "December",
  ],
  monthNamesShort: ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"],
  dayNames: ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"],
  dayNamesShort: ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"],
  today: "Vandaag",
};
LocaleConfig.defaultLocale = "en";

/**
 * This is from the react-native-calendars library but we defined it ourselves because it is not exported.
 */
export type MarkedDatesType = {
  [key: string]: MarkingProps;
};

const DEFAULT_NUM_DAY_FOR_GROCERY_LIST = 7;

interface GroceryListDatePickerProps {
  createGroceryList: (startDate: moment.Moment, endDate: moment.Moment) => Promise<void>;
  isCreating?: boolean;
}

const AddDateRange = (props: GroceryListDatePickerProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { colors } = theme;

  const { createGroceryList, isCreating = false } = props;

  const language = i18n.language.split("-")[0]; // Use the primary language code (e.g., 'en' from 'en-US')
  LocaleConfig.defaultLocale = LocaleConfig.locales[language] ? language : "en";

  const initialStartDate = moment();
  // NOTE: Minus 1 because people expect it to be exclusive, not inclusive (ie, Monday - Sunday, not Monday - Monday)
  const initialEndDate = moment().add(DEFAULT_NUM_DAY_FOR_GROCERY_LIST - 1, "days");
  const [startDate, setStartDate] = useState<moment.Moment>(initialStartDate);
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(initialEndDate);

  const markedDateBaseProperties: MarkingProps = {
    color: colors.primary["50"],
    textColor: "white",
  };

  const initialGroceryListRange = moment.range(moment(initialStartDate), moment(initialEndDate)).by("days");
  const rangeArray = Array.from(initialGroceryListRange);
  const defaultMarkedDates: MarkedDatesType = _.fromPairs(
    rangeArray.map((m, index) => {
      const dateStr = m.format("YYYY-MM-DD");
      const isFirst = index === 0;
      const isLast = index === rangeArray.length - 1;
      return [
        dateStr,
        {
          ...markedDateBaseProperties,
          ...(isFirst && { startingDay: true }),
          ...(isLast && { endingDay: true }),
        },
      ];
    })
  );

  const [markedDates, setMarkedDates] = useState<MarkedDatesType>(defaultMarkedDates);

  const onDayPress = (day: DateData): void => {
    if (!startDate || (startDate && endDate)) {
      const updatedMarkedDates: MarkedDatesType = {
        [day.dateString]: { ...markedDateBaseProperties, startingDay: true, endingDay: true },
      };
      setMarkedDates(updatedMarkedDates);
      setEndDate(undefined);
      setStartDate(moment(day.dateString));
    } else {
      const updatedMarkedDates = _.cloneDeep(markedDates);
      const calculatedEndDate = moment(day.dateString);
      const numDaysInPeriod = calculatedEndDate.diff(startDate, "days");

      if (numDaysInPeriod > 0) {
        // Clear existing marks
        const newMarkedDates: MarkedDatesType = {};

        // Mark the starting day
        newMarkedDates[startDate.format("YYYY-MM-DD")] = {
          ...markedDateBaseProperties,
          startingDay: true,
          endingDay: numDaysInPeriod === 0,
        };

        // Mark days in between
        const startDateCopy = moment(startDate);
        for (let i = 1; i < numDaysInPeriod; i += 1) {
          startDateCopy.add(1, "day");
          newMarkedDates[startDateCopy.format("YYYY-MM-DD")] = markedDateBaseProperties;
        }

        // Mark the ending day
        newMarkedDates[day.dateString] = {
          ...markedDateBaseProperties,
          endingDay: true,
        };

        setMarkedDates(newMarkedDates);
        setEndDate(calculatedEndDate);
      } else {
        // A date before the startDate was picked, thus reset the startDate
        setStartDate(moment(day.dateString));
        setEndDate(undefined);
        setMarkedDates({
          [day.dateString]: { ...markedDateBaseProperties, startingDay: true, endingDay: true },
        });
      }
    }
  };

  const shouldShowCreateButton = startDate && endDate;
  const disableArrowLeft = false;

  return (
    <VStack space={6} testID="groceryListDatePicker-div">
      <Box borderRadius={theme.borderRadii.card.default} overflow="hidden" bg={colors.primary["10"]}>
        <Calendar
          monthFormat="MMMM"
          onDayPress={onDayPress}
          style={{
            marginHorizontal: 0,
            marginVertical: Scale(16),
          }}
          markingType="period"
          markedDates={markedDates}
          theme={{
            backgroundColor: colors.primary["10"],
            calendarBackground: colors.primary["10"],
            textSectionTitleColor: colors.function["30"],
            selectedDayTextColor: "#ffffff",
            todayTextColor: colors.primary["50"],
            dayTextColor: colors.function["40"],
            textDisabledColor: colors.function["20"],
            arrowColor: colors.primary["50"],
            disabledArrowColor: colors.function["20"],
            monthTextColor: colors.function["50"],
            textDayFontFamily: theme.fonts.body,
            textMonthFontFamily: theme.fonts.heading,
            textDayHeaderFontFamily: theme.fonts.body,
            textDayFontSize: Scale(14),
            textMonthFontSize: Scale(18),
            textDayHeaderFontSize: Scale(14),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ["stylesheet.day.period" as any]: {
              base: {
                overflow: "hidden",
                height: 34,
                alignItems: "center",
                width: 38,
              },
              fillers: {
                position: "absolute",
                height: 34,
                flexDirection: "row",
                left: 0,
                right: 0,
              },
              leftFiller: {
                height: 34,
                flex: 1,
              },
              rightFiller: {
                height: 34,
                flex: 1,
              },
              startingDay: {
                borderTopLeftRadius: theme.borderRadii.button.default,
                borderBottomLeftRadius: theme.borderRadii.button.default,
              },
              endingDay: {
                borderTopRightRadius: theme.borderRadii.button.default,
                borderBottomRightRadius: theme.borderRadii.button.default,
              },
            },
          }}
          renderArrow={(direction) => (
            <MaterialIcons
              name={direction === "left" ? "chevron-left" : "chevron-right"}
              size={28}
              color={direction === "left" && disableArrowLeft ? colors.function["20"] : colors.primary["50"]}
            />
          )}
          disableArrowLeft={disableArrowLeft}
          firstDay={1}
        />
      </Box>

      {shouldShowCreateButton ? (
        <Box width="55%" alignSelf="center">
          <Button
            size="lg"
            bg={colors.primary["50"]}
            _hover={{ bg: colors.primary["50"] }}
            _pressed={{ bg: colors.primary["50"] }}
            _text={{ color: "white" }}
            borderRadius={theme.borderRadii.button.default}
            width="100%"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onPress={async () => {
              if (!startDate) {
                // TODO: Create helper function that will throw an error but also surface an alert to the user
                throw new Error("Start date is not set");
              }
              if (!endDate) {
                throw new Error("End date is not set");
              }

              await createGroceryList(startDate, endDate);
            }}
            testID={"createGroceryListDatePicker-button"}
            isLoading={isCreating}
            isDisabled={!startDate || !endDate || isCreating}
          >
            {t("grocery_list.create_list_button")}
          </Button>
        </Box>
      ) : null}
    </VStack>
  );
};
export default AddDateRange;

// NOTE: This is here for future reference
// const styles = StyleSheet.create({
//   header: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     borderTopWidth: Scale(1),
//     paddingVertical: Scale(20),
//     paddingLeft: Scale(14),
//     paddingRight: Scale(20),
//     borderTopColor: Colors.disableButton,
//     borderBottomColor: Colors.disableButton,
//     borderBottomWidth: Scale(1),
//   },
//   headerText: {
//     fontFamily: FontFamily.medium,
//     fontSize: Scale(16),
//   },
// });
